import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import { format } from "date-fns";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import DateFormField from "components/date-form-field";
import ErrorSnackbarContent from "components/error-snackbar-content";
import ModalForm from "components/modal-form";
import { FITTING_DELIVERY_DATE_SELECTOR } from "constants/feature-flags";
import {
  PATIENT_PRODUCT_STATUS_CANCELED,
  PATIENT_PRODUCT_STATUS_DELIVERED,
  PATIENT_PRODUCT_STATUS_ON_LOAN,
  PATIENT_PRODUCT_STATUS_REPAIR_REQUESTED,
  PATIENT_PRODUCT_STATUS_RETURNED,
  PATIENT_PRODUCT_STATUS_SHIPPED_TO_MANUFACTURER
} from "constants/patient-products";
import { selectCurrentPractice } from "features/auth/auth-slice";
import {
  patientProductsApi,
  useGetPatientProductsQuery,
  useUpdatePatientProductMutation
} from "services/patient-products";
import {
  usePreviewPatientProductsDeliveryMutation,
  useUpdatePatientProductsDeliverHearingAidsMutation
} from "services/patients";
import { useGetProductQuery } from "services/products";
import type { TinyAppointment } from "services/types/appointments.type";
import { formatDateOnlyValue } from "utils/common";
import { ActivePatientProduct } from "utils/patient-products.util";

import SelectFittingAppointmentField, {
  appointmentToDeliveryDate,
  usePatientFittingAppointments
} from "../../select-fitting-appointment-field";
import RedeliveryDateModal, {
  type RedeliveryDevice
} from "./redelivery-date-modal.component";
import {
  WarrantyDatesOnDeliveryControl,
  WarrantyDatesOnDeliveryPreviewProduct
} from "./warranty-dates-on-delivery-control.component";
import { WarrantyHelperText } from "./warranty-helper-text.component";

const WARRANTY_PREVIEW_QUERY =
  "{id,description,status,manufacturer_warranty_expiration_date,loss_damage_warranty_expiration_date,replaced_patient_product_detail,loss_damage_used_at_display,inventory_product{product{display_name,specification}}}";

const formatDeviceList = (names: string[]) =>
  names.length <= 1
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

interface MarkProductsAsDeliveredModalProps {
  patientId: number;
  isOpen: boolean;
  onCancel: () => void;
  product?: ActivePatientProduct;
  refetchPatient?: () => void;
  defaultAppointmentId?: number;
}

export const MarkProductsAsDeliveredModal: React.FC<
  MarkProductsAsDeliveredModalProps
> = ({
  onCancel,
  isOpen,
  product,
  patientId,
  refetchPatient,
  defaultAppointmentId
}) => {
  const productId = product?.id;
  const dispatch = useAppDispatch();
  const currentPractice = useAppSelector(selectCurrentPractice);
  const fittingSelectorEnabled = Boolean(
    useFlags()[FITTING_DELIVERY_DATE_SELECTOR]
  );
  const {
    appointments: fittingAppointments,
    mostRecent: mostRecentFittingAppointment,
    isLoading: fittingAppointmentsLoading
  } = usePatientFittingAppointments(patientId, {
    skip: !fittingSelectorEnabled
  });

  const manufacturer_warranty = product?.manufacturer_warranty;
  const lost_and_damage_warranty = product?.lost_and_damage_warranty;

  const { data } = useGetProductQuery(
    {
      id: Number(product?.inventory_product?.product.id),
      // cpt_codes not needed in this flow; pass `false` explicitly so the
      // RTK cache key aligns with other call sites and doesn't fragment.
      multipleCptCodesFeature: false
    },
    {
      skip: !product?.inventory_product?.product.id
    }
  );

  const defaultValues = useMemo(() => {
    const delivered_at = new Date();

    const manufacturer_warranty_expiration_date = manufacturer_warranty
      ? formatDateOnlyValue(manufacturer_warranty) || null
      : data?.specification?.warranty_months
        ? new Date(
            new Date().setMonth(
              new Date().getMonth() + data?.specification?.warranty_months
            )
          )
        : null;

    const loss_damage_warranty_expiration_date = lost_and_damage_warranty
      ? formatDateOnlyValue(lost_and_damage_warranty) || null
      : data?.specification?.loss_damage_warranty_months
        ? new Date(
            new Date().setMonth(
              new Date().getMonth() +
                data?.specification?.loss_damage_warranty_months
            )
          )
        : null;

    return {
      delivered_at,
      manufacturer_warranty_expiration_date,
      loss_damage_warranty_expiration_date,
      appointment: null as TinyAppointment | null,
      update_warranty_dates:
        currentPractice?.warranty_dates_on_delivery_default ?? false
    };
  }, [
    data,
    manufacturer_warranty,
    lost_and_damage_warranty,
    currentPractice?.warranty_dates_on_delivery_default
  ]);

  const formMethods = useForm<{
    delivered_at: Date;
    manufacturer_warranty_expiration_date: Date | string | null;
    loss_damage_warranty_expiration_date: Date | string | null;
    // Narrowed: the full TinyAppointment makes tsc's FieldPath union unbounded (ENG-2531).
    appointment: Pick<TinyAppointment, "id"> | null;
    update_warranty_dates: boolean;
  }>({ defaultValues });

  const { control, setValue, getValues, reset } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const [updatePatientProduct, { isLoading: productIsSubmitting }] =
    useUpdatePatientProductMutation();

  const [
    updatePatientProductsDeliverHearingAids,
    { isLoading: productsIsSubmitting }
  ] = useUpdatePatientProductsDeliverHearingAidsMutation();

  const [previewPatientProductsDelivery, { isLoading: previewIsLoading }] =
    usePreviewPatientProductsDeliveryMutation();
  const [redeliveryDevices, setRedeliveryDevices] = useState<
    RedeliveryDevice[]
  >([]);

  const submitDelivery = useCallback(
    (overrides?: Record<string, string | null>) => {
      // null passes straight through: it is an explicit "remove this date", not a missing value.
      const delivered_at_overrides = Object.fromEntries(
        Object.entries(overrides ?? {}).map(([id, value]) => [
          id,
          value === null ? null : format(new Date(value), "yyyy-MM-dd")
        ])
      );
      updatePatientProductsDeliverHearingAids({
        patientId: Number(patientId),
        delivered_at: format(new Date(getValues("delivered_at")), "yyyy-MM-dd"),
        update_warranty_dates: getValues("update_warranty_dates"),
        ...(Object.keys(delivered_at_overrides).length
          ? { delivered_at_overrides }
          : {})
      }).then((response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
          dispatch(setOpen(true));
        } else {
          dispatch(patientProductsApi.util.invalidateTags(["PatientProducts"]));
          refetchPatient?.();
          dispatch(setCustomMessage("Active Hearing Aids delivered!"));
          dispatch(setOpen(true));
          onCancel();
        }
      });
    },
    [
      patientId,
      updatePatientProductsDeliverHearingAids,
      dispatch,
      getValues,
      onCancel,
      refetchPatient
    ]
  );

  // Ask the server what each device's date would become before writing anything. If any of them
  // has been delivered before, the user gets to see the history and set dates; otherwise this is
  // invisible. A failed preview must never block a delivery — fall through and deliver.
  const handleDeliverHearingAids = useCallback(() => {
    previewPatientProductsDelivery({
      patientId: Number(patientId),
      delivered_at: format(new Date(getValues("delivered_at")), "yyyy-MM-dd"),
      update_warranty_dates: getValues("update_warranty_dates")
    }).then((response) => {
      if ("error" in response) {
        submitDelivery();
        return;
      }
      const previouslyDelivered = response.data.devices.filter(
        (device) => device.was_delivered_before
      );
      if (previouslyDelivered.length > 0) {
        setRedeliveryDevices(previouslyDelivered);
        return;
      }
      submitDelivery();
    });
  }, [patientId, previewPatientProductsDelivery, getValues, submitDelivery]);

  // Loss & Damage replacement (SN-7365): its warranty dates are the original
  // device's and must not be edited on delivery — the fields are hidden and
  // the request omits them.
  const isLdReplacement = Boolean(product?.replaced_patient_product_detail);

  const handleDeliverPatientProduct = useCallback(
    (id: number) => {
      const {
        manufacturer_warranty_expiration_date,
        loss_damage_warranty_expiration_date,
        delivered_at
      } = getValues();
      updatePatientProduct({
        id: Number(id),
        status: PATIENT_PRODUCT_STATUS_DELIVERED,
        delivered_at: format(new Date(delivered_at), "yyyy-MM-dd"),
        ...(isLdReplacement
          ? {}
          : {
              manufacturer_warranty_expiration_date:
                manufacturer_warranty_expiration_date
                  ? format(
                      new Date(manufacturer_warranty_expiration_date),
                      "yyyy-MM-dd"
                    )
                  : null,
              loss_damage_warranty_expiration_date:
                loss_damage_warranty_expiration_date
                  ? format(
                      new Date(loss_damage_warranty_expiration_date),
                      "yyyy-MM-dd"
                    )
                  : null
            })
      }).then((response) => {
        if ("error" in response) {
          dispatch(
            setCustomMessage(<ErrorSnackbarContent response={response} />)
          );
          dispatch(setOpen(true));
        } else {
          refetchPatient?.();
          dispatch(patientProductsApi.util.invalidateTags(["PatientProducts"]));
          dispatch(setCustomMessage("Hearing aid is delivered to patient"));
          dispatch(setOpen(true));
          onCancel();
        }
      });
    },

    [
      dispatch,
      refetchPatient,
      onCancel,
      updatePatientProduct,
      getValues,
      isLdReplacement
    ]
  );

  // previewIsLoading counts: the dry run happens between the click and the modal, and without
  // it the button sits idle for a round trip with nothing explaining the pause.
  const isSubmitting =
    productIsSubmitting || productsIsSubmitting || previewIsLoading;

  useEffect(() => {
    setValue("delivered_at", new Date());
  }, [setValue, isOpen]);

  const repairRequested = Boolean(
    product?.statuses?.find((item) =>
      [
        PATIENT_PRODUCT_STATUS_REPAIR_REQUESTED,
        PATIENT_PRODUCT_STATUS_SHIPPED_TO_MANUFACTURER,
        PATIENT_PRODUCT_STATUS_CANCELED,
        PATIENT_PRODUCT_STATUS_RETURNED
      ].includes(item.value)
    )
  );

  const watchedDeliveredAt = useWatch({ control, name: "delivered_at" });

  const { data: warrantyPreviewProducts = [] } = useGetPatientProductsQuery(
    {
      patientId: Number(patientId),
      is_active: true,
      limit: 100,
      query: WARRANTY_PREVIEW_QUERY
    },
    { skip: Boolean(productId) || !isOpen }
  );

  const onLoanDeviceNames = useMemo(
    () =>
      warrantyPreviewProducts
        .filter(
          (previewProduct: WarrantyDatesOnDeliveryPreviewProduct) =>
            previewProduct.status === PATIENT_PRODUCT_STATUS_ON_LOAN
        )
        .map(
          (previewProduct: WarrantyDatesOnDeliveryPreviewProduct) =>
            previewProduct.inventory_product?.product?.display_name ||
            previewProduct.description ||
            "Unnamed device"
        ),
    [warrantyPreviewProducts]
  );

  const recomputeWarrantyDates = useCallback(
    (value: Date | null) => {
      if (repairRequested) return;

      const warrantyValue = value ? new Date(value) : value;
      const lossDamageWarrantyValue = value ? new Date(value) : value;

      const hasInitialWarrantyDate = Boolean(manufacturer_warranty);
      const hasInitialLossDamageWarrantyDate = Boolean(
        lost_and_damage_warranty
      );

      if (!hasInitialWarrantyDate && data?.specification?.warranty_months) {
        setValue(
          "manufacturer_warranty_expiration_date",
          warrantyValue
            ? new Date(
                warrantyValue.setMonth(
                  warrantyValue.getMonth() + data.specification.warranty_months
                )
              )
            : warrantyValue
        );
      }

      if (
        !hasInitialLossDamageWarrantyDate &&
        data?.specification?.loss_damage_warranty_months
      ) {
        setValue(
          "loss_damage_warranty_expiration_date",
          lossDamageWarrantyValue
            ? new Date(
                lossDamageWarrantyValue.setMonth(
                  lossDamageWarrantyValue.getMonth() +
                    data.specification.loss_damage_warranty_months
                )
              )
            : lossDamageWarrantyValue
        );
      }
    },
    [
      repairRequested,
      manufacturer_warranty,
      lost_and_damage_warranty,
      data,
      setValue
    ]
  );

  const handleSelectFittingAppointment = useCallback(
    (appointment: TinyAppointment | null) => {
      setValue("appointment", appointment);
      const deliveredDate = appointmentToDeliveryDate(appointment);
      if (deliveredDate) {
        setValue("delivered_at", deliveredDate);
        recomputeWarrantyDates(deliveredDate);
      }
    },
    [setValue, recomputeWarrantyDates]
  );

  const fittingDefaultAppliedRef = useRef(false);

  useEffect(() => {
    fittingDefaultAppliedRef.current = false;
  }, [isOpen, patientId, defaultValues]);

  useEffect(() => {
    if (!fittingSelectorEnabled || !isOpen) return;
    if (fittingDefaultAppliedRef.current) return;
    if (fittingAppointmentsLoading) return;

    fittingDefaultAppliedRef.current = true;

    if (!fittingAppointments.length) return;

    const inContextAppointment = defaultAppointmentId
      ? fittingAppointments.find((appt) => appt.id === defaultAppointmentId)
      : undefined;
    const defaultAppointment =
      inContextAppointment ?? mostRecentFittingAppointment;

    if (!defaultAppointment) return;

    setValue("appointment", defaultAppointment);
    const deliveredDate = appointmentToDeliveryDate(defaultAppointment);
    if (deliveredDate) {
      setValue("delivered_at", deliveredDate);
      recomputeWarrantyDates(deliveredDate);
    }
  }, [
    fittingSelectorEnabled,
    isOpen,
    fittingAppointmentsLoading,
    fittingAppointments,
    mostRecentFittingAppointment,
    defaultAppointmentId,
    setValue,
    recomputeWarrantyDates
  ]);

  return (
    <FormProvider {...formMethods}>
      <ModalForm
        title={
          productId
            ? `Mark Delivered to Patient`
            : `Mark All Products Delivered`
        }
        hideBackdrop={false}
        isOpen={isOpen}
        onCancel={onCancel}
        onSubmit={() =>
          productId
            ? handleDeliverPatientProduct(productId)
            : handleDeliverHearingAids()
        }
        isSubmitting={isSubmitting}
        maxWidth="lg"
        showCancelButton
        submitButtonLabel="Save"
        dialogActionsSx={{
          flexDirection: "row-reverse",
          justifyContent: "end",
          gap: 2
        }}
      >
        <Stack gap={3}>
          {fittingSelectorEnabled && (
            <Box paddingX={0.5}>
              <SelectFittingAppointmentField
                control={control}
                name="appointment"
                appointments={fittingAppointments}
                isLoading={fittingAppointmentsLoading}
                onChange={handleSelectFittingAppointment}
              />
            </Box>
          )}
          <Stack paddingX={0.5} flexDirection="row" alignItems="end" gap={2}>
            <DateFormField
              name="delivered_at"
              label="Delivered At"
              control={control}
              required
              onChange={(value) => {
                setValue("appointment", null);
                recomputeWarrantyDates(value ?? null);
              }}
              sx={{}}
            />
          </Stack>
          {!productId && onLoanDeviceNames.length > 0 && (
            <Box paddingX={0.5}>
              <Alert severity="info" data-testid="on_loan_skipped_notice">
                {formatDeviceList(onLoanDeviceNames)}{" "}
                {onLoanDeviceNames.length === 1 ? "is" : "are"} on loan and will
                not be marked as delivered.
              </Alert>
            </Box>
          )}
          {!productId && (
            <Box paddingX={0.5}>
              <WarrantyDatesOnDeliveryControl
                products={warrantyPreviewProducts}
                deliveredAt={watchedDeliveredAt ?? null}
                control={control}
              />
            </Box>
          )}
          {product && isLdReplacement && (
            <Alert
              severity="warning"
              data-testid="ld-replacement-warranty-notice"
              sx={{ borderRadius: 2 }}
            >
              This is the Loss &amp; Damage replacement for SN{" "}
              {product.replaced_patient_product_detail?.serial_number || "—"}.
              Its manufacturer and extended warranty dates were carried over
              from the original device
              {product.loss_damage_used_at_display
                ? ` and the L&D benefit was used on ${product.loss_damage_used_at_display}`
                : ""}
              . Warranty dates are not updated when an L&D replacement is
              delivered.
            </Alert>
          )}
          {product && !repairRequested && !isLdReplacement && (
            <Box
              sx={{
                borderRadius: 5,
                border: "2px solid #2e9e44",
                width: "max-content",
                ul: { paddingLeft: "40px", paddingRight: "20px" }
              }}
            >
              <WarrantyHelperText />
            </Box>
          )}
          {product && !repairRequested && !isLdReplacement && (
            <>
              <DateFormField
                name="manufacturer_warranty_expiration_date"
                label="MFR Warranty Date"
                control={control}
                sx={{}}
              />
              <Box
                sx={{
                  borderRadius: 5,
                  border: "2px solid #2e9e44",
                  width: "max-content",
                  ul: { paddingLeft: "40px", paddingRight: "20px" }
                }}
              >
                <ul>
                  <li>
                    Current MFR warranty date:{" "}
                    {manufacturer_warranty
                      ? formatDateOnlyValue(
                          manufacturer_warranty,
                          "MMM dd, yyyy"
                        )
                      : "-"}
                  </li>

                  <li>
                    Current MFR warranty months:{" "}
                    {data?.specification?.warranty_months || "-"}
                  </li>
                </ul>
              </Box>

              <DateFormField
                name="loss_damage_warranty_expiration_date"
                label="L&D Warranty Date"
                control={control}
                sx={{}}
              />

              <Box
                sx={{
                  borderRadius: 5,
                  border: "2px solid #2e9e44",
                  width: "max-content",
                  ul: { paddingLeft: "40px", paddingRight: "20px" }
                }}
              >
                <ul>
                  <li>
                    Current L&D warranty date{" "}
                    {lost_and_damage_warranty
                      ? formatDateOnlyValue(
                          lost_and_damage_warranty,
                          "MMM dd, yyyy"
                        )
                      : "-"}
                  </li>

                  <li>
                    Current L&D warranty months:{" "}
                    {data?.specification?.loss_damage_warranty_months || "-"}
                  </li>
                </ul>
              </Box>
            </>
          )}
        </Stack>
      </ModalForm>

      <RedeliveryDateModal
        open={redeliveryDevices.length > 0}
        devices={redeliveryDevices}
        isSubmitting={productsIsSubmitting}
        onDeliver={(overrides) => {
          setRedeliveryDevices([]);
          submitDelivery(overrides);
        }}
        onCancel={() => setRedeliveryDevices([])}
      />
    </FormProvider>
  );
};
